import axios from 'axios';
import React, { useEffect, useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import ConfirmModal from './common/ConfirmModal';
import './ProductManager.css';

const ProductManager = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    currency: 'INR',
    discount: 0,
    category: '',
    subcategory: '',
    images: [],
    stock: '',
    weight: '',
    dimensions: {
      length: '',
      width: '',
      height: ''
    },
    moreInfo: '',
    careGuide: '',
    isActive: true,
    likeCount: 0,
    hasVariants: false,
    variants: []
  });
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [variantForm, setVariantForm] = useState({
    attributes: [],
    price: '',
    discount: 0,
    stock: '',
    sku: '',
    image: '',
    isActive: true
  });
  const [editingVariantIndex, setEditingVariantIndex] = useState(null);
  const [homeImageFile, setHomeImageFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [uploadingHomeImage, setUploadingHomeImage] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });


  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAttributes();
  }, []);

  const fetchAttributes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/product-attributes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAttributes(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch attributes:', error);
      setAvailableAttributes([]);
    }
  };

  // Use useRef to track previous category value and prevent unnecessary updates
  const prevCategoryRef = useRef(formData.category);
  
  useEffect(() => {
    // Only run if category actually changed
    if (prevCategoryRef.current === formData.category) {
      return; // Skip if category hasn't changed
    }
    
    prevCategoryRef.current = formData.category;
    
    if (formData.category) {
      fetchSubcategories(formData.category);
    } else {
      setSubcategories([]);
      // Only update subcategory if it's not already empty to prevent unnecessary state updates
      setFormData(prev => {
        if (prev.subcategory === '') {
          return prev; // Return same object if no change needed
        }
        return { ...prev, subcategory: '' };
      });
    }
  }, [formData.category]); // Only depend on category, not entire formData

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`);
      setProducts(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchSubcategories = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/subcategories?category=${categoryId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubcategories(response.data.data);
    } catch (error) {
      // Silently fail - don't show error toast for subcategory fetch
      // This prevents authentication errors from interrupting form filling
      console.warn('Failed to fetch subcategories:', error);
      setSubcategories([]);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/products/import/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'product_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to download template');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/products/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Products exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to export products');
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/products/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `products_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Products exported successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to export products');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/products/import`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        const { imported, failed, errors } = response.data.data;
        toast.success(`Import completed! ${imported} imported, ${failed} failed.`);
        
        if (errors.length > 0) {
          console.error('Import errors:', errors);
          toast.warning(`${errors.length} errors occurred. Check console for details.`);
        }
        
        fetchProducts(); // Refresh product list
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to import products');
    } finally {
      // Reset file input
      e.target.value = '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Use functional update to prevent stale closures and unnecessary re-renders
    // This ensures state updates don't interfere with each other while typing
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleHomeImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setHomeImageFile(e.target.files[0]);
    }
  };

  const handleCoverImageSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCoverImageFile(e.target.files[0]);
    }
  };

  const handleUploadCoverImage = async () => {
    if (!coverImageFile) {
      toast.error('Please select a cover image');
      return;
    }

    setUploadingCoverImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('images', coverImageFile);
    
    // Build query string for upload type and product info
    let queryString = '?uploadType=product';
    if (editingProduct) {
      queryString += `&productId=${editingProduct._id}`;
    } else {
      const selectedCategory = categories.find(cat => 
        cat._id === formData.category || 
        cat._id === formData.category?._id
      );
      const selectedSubcategory = subcategories.find(sub => 
        sub._id === formData.subcategory ||
        sub._id === formData.subcategory?._id
      );
      
      if (selectedCategory) {
        queryString += `&categoryName=${encodeURIComponent(selectedCategory.name)}`;
      }
      if (selectedSubcategory) {
        queryString += `&subcategoryName=${encodeURIComponent(selectedSubcategory.name)}`;
      }
      if (formData.name) {
        queryString += `&productName=${encodeURIComponent(formData.name)}`;
      }
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload/multiple${queryString}`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.data && response.data.data.length > 0) {
        // Cover image is the first image (index 0) - use baseS3Url
        const newImages = [response.data.data[0].baseS3Url || response.data.data[0].url, ...formData.images];
        setFormData({
          ...formData,
          images: newImages
        });
        setCoverImageFile(null);
        toast.success('Cover image uploaded successfully!');
      }
    } catch (error) {
      console.error('Cover image upload error:', error);
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingCoverImage(false);
    }
  };

  const handleUploadHomeImage = async () => {
    if (!homeImageFile) {
      toast.error('Please select a home image');
      return;
    }

    setUploadingHomeImage(true);
    const formDataUpload = new FormData();
    formDataUpload.append('images', homeImageFile);
    
    // Build query string for upload type and product info
    let queryString = '?uploadType=product';
    if (editingProduct) {
      queryString += `&productId=${editingProduct._id}`;
    } else {
      const selectedCategory = categories.find(cat => 
        cat._id === formData.category || 
        cat._id === formData.category?._id
      );
      const selectedSubcategory = subcategories.find(sub => 
        sub._id === formData.subcategory ||
        sub._id === formData.subcategory?._id
      );
      
      if (selectedCategory) {
        queryString += `&categoryName=${encodeURIComponent(selectedCategory.name)}`;
      }
      if (selectedSubcategory) {
        queryString += `&subcategoryName=${encodeURIComponent(selectedSubcategory.name)}`;
      }
      if (formData.name) {
        queryString += `&productName=${encodeURIComponent(formData.name)}`;
      }
    }

    try {
      const response = await axios.post(`${API_URL}/api/upload/multiple${queryString}`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.data && response.data.data.length > 0) {
        // Home images are additional images (after the first/cover image) - use baseS3Url
        const coverImage = formData.images[0] || null;
        const additionalImages = formData.images.slice(1);
        const newImages = coverImage 
          ? [coverImage, ...additionalImages, response.data.data[0].baseS3Url || response.data.data[0].url]
          : [...formData.images, response.data.data[0].baseS3Url || response.data.data[0].url];
        
        setFormData({
          ...formData,
          images: newImages
        });
        setHomeImageFile(null);
        toast.success('Home image uploaded successfully!');
      }
    } catch (error) {
      console.error('Home image upload error:', error);
      toast.error(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploadingHomeImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const handleRemoveCoverImage = () => {
    // Remove the first image (cover image)
    if (formData.images.length > 0) {
      setFormData({
        ...formData,
        images: formData.images.slice(1)
      });
    }
  };

  const handleRemoveHomeImage = (index) => {
    // Remove home image (index + 1 because index 0 is cover image)
    const actualIndex = index + 1;
    if (actualIndex < formData.images.length) {
      setFormData({
        ...formData,
        images: formData.images.filter((_, i) => i !== actualIndex)
      });
    }
  };

  const handleSubmit = async (e) => {
    // Prevent any default form behavior and event bubbling
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      // Validate required fields
      if (!formData.name || !formData.price || !formData.category) {
        toast.error('Please fill in all required fields (Name, Price, Category)');
        return false; // Explicitly return false
      }

      // Validate variants if enabled
      if (formData.hasVariants && (!formData.variants || formData.variants.length === 0)) {
        toast.error('Please add at least one variant when variants are enabled');
        return false; // Explicitly return false
      }

      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        discount: parseFloat(formData.discount) || 0,
        stock: parseInt(formData.stock) || 0,
        subcategory: formData.subcategory && formData.subcategory.trim() ? formData.subcategory : null,
        weight: formData.weight ? parseFloat(formData.weight) : 0,
        likeCount: parseInt(formData.likeCount) || 0,
        hasVariants: formData.hasVariants || false,
        variants: formData.hasVariants ? (formData.variants || []) : [],
        dimensions: {
          length: formData.dimensions?.length ? parseFloat(formData.dimensions.length) : 0,
          width: formData.dimensions?.width ? parseFloat(formData.dimensions.width) : 0,
          height: formData.dimensions?.height ? parseFloat(formData.dimensions.height) : 0
        }
      };

      // Remove empty strings and undefined values that might cause validation issues
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === undefined) {
          if (key !== 'subcategory' && key !== 'description' && key !== 'moreInfo' && key !== 'careGuide') {
            delete submitData[key];
          }
        }
      });

      // Clean up undefined values from variants
      if (submitData.variants && submitData.variants.length > 0) {
        submitData.variants = submitData.variants.map(variant => {
          const cleanedVariant = {
            attributes: variant.attributes || [],
            isActive: variant.isActive !== undefined ? variant.isActive : true
          };
          if (variant.price !== undefined && variant.price !== '') {
            cleanedVariant.price = parseFloat(variant.price);
          }
          if (variant.discount !== undefined && variant.discount > 0) {
            cleanedVariant.discount = parseFloat(variant.discount);
            // Calculate discounted price
            if (cleanedVariant.price !== undefined) {
              cleanedVariant.discountedPrice = cleanedVariant.price * (1 - cleanedVariant.discount / 100);
            }
          }
          if (variant.discountedPrice !== undefined) {
            cleanedVariant.discountedPrice = parseFloat(variant.discountedPrice);
          }
          if (variant.stock !== undefined && variant.stock !== '') {
            cleanedVariant.stock = parseInt(variant.stock);
          }
          if (variant.sku && variant.sku.trim()) {
            cleanedVariant.sku = variant.sku.trim();
          }
          if (variant.image && variant.image.trim()) {
            cleanedVariant.image = variant.image.trim();
          }
          return cleanedVariant;
        });
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required. Please login again.');
        return false;
      }
      
      // Set loading state to prevent multiple submissions
      setLoading(true);
      
      try {
        if (editingProduct) {
          await axios.put(`${API_URL}/api/products/${editingProduct._id}`, submitData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Product updated successfully!');
        } else {
          await axios.post(`${API_URL}/api/products`, submitData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Product created successfully!');
        }
      } catch (apiError) {
        // Re-throw to be caught by outer catch block
        throw apiError;
      } finally {
        setLoading(false);
      }
      
      // Only close modal and reset form on successful submission
      setShowModal(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        currency: 'INR',
        discount: 0,
        category: '',
        subcategory: '',
        images: [],
        stock: '',
        weight: '',
        dimensions: {
          length: '',
          width: '',
          height: ''
        },
        moreInfo: '',
        careGuide: '',
        isActive: true,
        likeCount: 0,
        hasVariants: false,
        variants: []
      });
      setActiveTab('basic');
      setHomeImageFile(null);
      setCoverImageFile(null);
      setVariantForm({
        attributes: [],
        price: '',
        stock: '',
        sku: '',
        image: '',
        isActive: true
      });
      setEditingVariantIndex(null);
      fetchProducts();
      
      return false; // Prevent form submission
    } catch (error) {
      console.error('Product submission error:', error);
      
      // Handle authentication errors specifically
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        // Don't close modal - let user see the error
        // Don't redirect or refresh - just show error
        return false;
      }
      
      // Handle other errors
      const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Operation failed';
      toast.error(`Error: ${errorMessage}`);
      if (error.response?.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
      }
      
      // Don't close modal on error - keep form data so user can fix and retry
      // Don't refresh page - just show error message
      return false; // Prevent form submission
    }
  };

  const handleEdit = async (product) => {
    // Fetch fresh product data to get new pre-signed URLs
    try {
      const response = await axios.get(`${API_URL}/api/products/${product._id}`);
      const freshProduct = response.data.data;
      
      setEditingProduct(freshProduct);
      setFormData({
        name: freshProduct.name,
        description: freshProduct.description || '',
        price: freshProduct.price.toString(),
        currency: freshProduct.currency || 'INR',
        discount: freshProduct.discount || 0,
        category: freshProduct.category._id || freshProduct.category,
        subcategory: freshProduct.subcategory?._id || freshProduct.subcategory || '',
        images: freshProduct.images || [], // Fresh pre-signed URLs
        stock: freshProduct.stock.toString(),
        weight: freshProduct.weight ? freshProduct.weight.toString() : '',
        dimensions: {
          length: freshProduct.dimensions?.length ? freshProduct.dimensions.length.toString() : '',
          width: freshProduct.dimensions?.width ? freshProduct.dimensions.width.toString() : '',
          height: freshProduct.dimensions?.height ? freshProduct.dimensions.height.toString() : ''
        },
        moreInfo: freshProduct.moreInfo || '',
        careGuide: freshProduct.careGuide || '',
        isActive: freshProduct.isActive,
        likeCount: freshProduct.likeCount || 0,
        hasVariants: freshProduct.hasVariants || false,
        variants: freshProduct.variants || []
      });
      setActiveTab('basic');
      setVariantForm({
        attributes: [],
        price: '',
        discount: 0,
        stock: '',
        sku: '',
        image: '',
        isActive: true
      });
      setEditingVariantIndex(null);
      if (freshProduct.category._id || freshProduct.category) {
        fetchSubcategories(freshProduct.category._id || freshProduct.category);
      }
      setHomeImageFile(null);
      setCoverImageFile(null);
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load product details');
      console.error('Error fetching product:', error);
    }
  };

  const handleDelete = (id) => {
    setConfirmModal({
      show: true,
      title: 'Delete Product',
      message: 'Are you sure you want to delete this product? This action cannot be undone.',
      type: 'danger',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/api/products/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Product deleted successfully!');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
          fetchProducts();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Delete failed');
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      }
    });
  };

  const openModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      currency: 'INR',
      discount: 0,
      category: '',
      subcategory: '',
      images: [],
      stock: '',
      isActive: true,
      likeCount: 0,
      hasVariants: false,
      variants: []
    });
    setHomeImageFile(null);
    setCoverImageFile(null);
    setVariantForm({
      attributes: [],
      price: '',
      stock: '',
      sku: '',
      image: '',
      isActive: true
    });
    setEditingVariantIndex(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>Product Manager</h1>
        <div className="header-actions">
          <button onClick={handleDownloadTemplate} className="btn btn-secondary">
            📥 Download Template
          </button>
          <button onClick={handleExportCSV} className="btn btn-secondary">
            📤 Export CSV
          </button>
          <button onClick={handleExportExcel} className="btn btn-secondary">
            📤 Export Excel
          </button>
          <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
            📥 Import
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleImportFile}
              style={{ display: 'none' }}
            />
          </label>
          <button onClick={openModal} className="btn btn-primary">
            + Add Product
          </button>
        </div>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Subcategory</th>
              <th>Price</th>
              <th>Discount</th>
              <th>Stock</th>
              <th>Images</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center' }}>
                  No products found
                </td>
              </tr>
            ) : (
              products.map((product) => {
                const discountedPrice = product.discountedPrice || (product.price - (product.price * (product.discount || 0) / 100));
                return (
                <tr key={product._id}>
                  <td>{product.name}</td>
                  <td>{product.category?.name || '-'}</td>
                  <td>{product.subcategory?.name || '-'}</td>
                  <td>
                    {product.currency === 'INR' ? '₹' : product.currency === 'USD' ? '$' : '€'}
                    {product.price.toLocaleString('en-IN')}
                    {product.discount > 0 && (
                      <span style={{ color: '#28a745', marginLeft: '5px' }}>
                        (₹{discountedPrice.toLocaleString('en-IN')})
                      </span>
                    )}
                  </td>
                  <td>{product.discount > 0 ? `${product.discount}%` : '-'}</td>
                  <td>
                    {product.hasVariants ? (
                      <span style={{ color: '#007bff', fontWeight: 'bold' }}>
                        {product.variants?.length || 0} variants
                      </span>
                    ) : (
                      product.stock
                    )}
                  </td>
                  <td>
                    {product.images && product.images.length > 0 ? (
                      <div className="image-preview-list">
                        {product.images.slice(0, 3).map((img, idx) => (
                          <img 
                            key={idx} 
                            src={img} 
                            alt={`${product.name} ${idx + 1}`} 
                            className="thumbnail"
                            onError={(e) => {
                              console.error('Failed to load product image:', img);
                              e.target.style.display = 'none';
                            }}
                          />
                        ))}
                        {product.images.length > 3 && (
                          <span className="more-images">+{product.images.length - 3}</span>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>
                    <span className={`badge ${product.isActive ? 'badge-success' : 'badge-danger'}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      onClick={() => handleEdit(product)}
                      className="btn btn-secondary btn-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(product._id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="product-modal-overlay" onClick={closeModal}>
          <div className="product-modal-content product-modal-fullscreen" onClick={(e) => e.stopPropagation()}>
            <div className="product-modal-header">
              <h2>{editingProduct ? 'Edit Product' : 'Add Product'}</h2>
              <button className="product-modal-close" onClick={closeModal}>×</button>
            </div>
            
            {/* Tabs */}
            <div className="product-modal-tabs">
              <button
                type="button"
                className={`product-modal-tab ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                Basic Info
              </button>
              <button
                type="button"
                className={`product-modal-tab ${activeTab === 'images' ? 'active' : ''}`}
                onClick={() => setActiveTab('images')}
              >
                Images
              </button>
              <button
                type="button"
                className={`product-modal-tab ${activeTab === 'additional' ? 'active' : ''}`}
                onClick={() => setActiveTab('additional')}
              >
                Additional Info
              </button>
              <button
                type="button"
                className={`product-modal-tab ${activeTab === 'variants' ? 'active' : ''}`}
                onClick={() => setActiveTab('variants')}
              >
                Variants
              </button>
            </div>
            
            <form 
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubmit(e);
              }} 
              className="product-form-grid"
              noValidate
            >
              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div className="product-tab-content">
                  <div className="form-group product-form-full-width">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name || ''}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group product-form-full-width">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleInputChange}
                      rows="4"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Category *</label>
                    <select
                      name="category"
                      value={formData.category || ''}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((cat) => (
                        <option key={cat._id} value={cat._id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {formData.category && (
                    <div className="form-group">
                      <label>Subcategory</label>
                      <select
                        name="subcategory"
                        value={formData.subcategory || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">None</option>
                        {subcategories.map((subcat) => (
                          <option key={subcat._id} value={subcat._id}>
                            {subcat.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>Price (₹) *</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price || ''}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Currency</label>
                    <select
                      name="currency"
                      value={formData.currency || 'INR'}
                      onChange={handleInputChange}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Discount (%)</label>
                    <input
                      type="number"
                      name="discount"
                      value={formData.discount || 0}
                      onChange={handleInputChange}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    {formData.price && formData.discount > 0 && (
                      <small style={{ color: '#28a745', display: 'block', marginTop: '5px', fontWeight: '500' }}>
                        Discounted Price: ₹{(parseFloat(formData.price) - (parseFloat(formData.price) * parseFloat(formData.discount) / 100)).toLocaleString('en-IN')}
                      </small>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label>Stock *</label>
                    <input
                      type="number"
                      name="stock"
                      value={formData.stock || ''}
                      onChange={handleInputChange}
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Weight (kg)</label>
                    <input
                      type="number"
                      name="weight"
                      value={formData.weight || ''}
                      onChange={handleInputChange}
                      min="0"
                      step="0.01"
                      placeholder="0.5"
                    />
                    <small>Product weight in kilograms (for shipping calculation)</small>
                  </div>

                  <div className="form-group product-form-full-width">
                    <label>Dimensions (cm)</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                        type="number"
                        name="dimensions.length"
                        value={formData.dimensions?.length || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: {
                            ...formData.dimensions,
                            length: e.target.value
                          }
                        })}
                        min="0"
                        step="0.01"
                        placeholder="Length"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="number"
                        name="dimensions.width"
                        value={formData.dimensions?.width || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: {
                            ...formData.dimensions,
                            width: e.target.value
                          }
                        })}
                        min="0"
                        step="0.01"
                        placeholder="Width"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="number"
                        name="dimensions.height"
                        value={formData.dimensions?.height || ''}
                        onChange={(e) => setFormData({
                          ...formData,
                          dimensions: {
                            ...formData.dimensions,
                            height: e.target.value
                          }
                        })}
                        min="0"
                        step="0.01"
                        placeholder="Height"
                        style={{ flex: 1 }}
                      />
                    </div>
                    <small>Product dimensions in centimeters (for shipping calculation)</small>
                  </div>
                  
                  <div className="form-group product-form-full-width">
                    <label>Like Count</label>
                    <input
                      type="number"
                      name="likeCount"
                      value={formData.likeCount || 0}
                      onChange={handleInputChange}
                      min="0"
                      className="form-control"
                      placeholder="Number of likes"
                    />
                    <small className="form-text">Set the initial like count for this product</small>
                  </div>
                  
                  <div className="form-group product-form-full-width">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                      />
                      Active
                    </label>
                  </div>
                </div>
              )}

              {/* Images Tab */}
              {activeTab === 'images' && (
                <div className="product-tab-content">
                  <div className="product-images-section product-form-full-width">
                    <h3>Product Images</h3>
                
                {/* Cover Image Upload - First Image (images[0]) */}
                <div style={{ marginBottom: '25px', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
                    🖼️ Cover Image (Main Image - 1st Image)
                  </label>
                  <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                    <strong style={{ display: 'block', marginBottom: '5px', color: '#0066cc' }}>📐 Ideal Image Size:</strong>
                    <span style={{ fontSize: '13px', color: '#333' }}>
                      <strong>Cover Image:</strong> 1000 x 1000 pixels (1:1 square ratio) - Displayed on product cards and product detail page main image
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleCoverImageSelect}
                        style={{ 
                          marginBottom: '10px',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          width: '100%'
                        }}
                      />
                      {coverImageFile && (
                        <div>
                          <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                            ✓ {coverImageFile.name} selected
                          </p>
                          <button
                            type="button"
                            onClick={handleUploadCoverImage}
                            className="btn btn-primary"
                            disabled={uploadingCoverImage}
                            style={{ marginRight: '10px' }}
                          >
                            {uploadingCoverImage ? '⏳ Uploading...' : '⬆️ Upload Cover Image'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setCoverImageFile(null)}
                            className="btn btn-secondary"
                          >
                            Clear
                          </button>
                        </div>
                      )}
                    </div>
                    {formData.images[0] && (
                      <div style={{ position: 'relative', width: '150px', height: '150px', border: '2px solid #ddd', borderRadius: '8px', overflow: 'hidden', background: '#f0f0f0' }}>
                        <img 
                          src={formData.images[0]} 
                          alt="Cover Image" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            console.error('Failed to load cover image:', formData.images[0]);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div style="padding: 20px; text-align: center; color: #999;">Image failed to load</div>';
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleRemoveCoverImage}
                          className="btn-remove-image"
                          title="Remove cover image"
                        >
                          ×
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Home Images Upload - Additional Images (images[1] onwards) */}
                <div style={{ marginBottom: '25px', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
                    🏠 Home Images (Additional Images)
                  </label>
                  <div style={{ marginBottom: '10px', padding: '10px', background: '#f0f7ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                    <strong style={{ display: 'block', marginBottom: '5px', color: '#0066cc' }}>📐 Ideal Image Size:</strong>
                    <span style={{ fontSize: '13px', color: '#333' }}>
                      <strong>Additional Images:</strong> 1000 x 1000 pixels (1:1 square ratio) - Displayed as thumbnails on product detail page
                    </span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleHomeImageSelect}
                      style={{ 
                        marginBottom: '10px',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        width: '100%'
                      }}
                    />
                    {homeImageFile && (
                      <div>
                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                          ✓ {homeImageFile.name} selected
                        </p>
                        <button
                          type="button"
                          onClick={handleUploadHomeImage}
                          className="btn btn-primary"
                          disabled={uploadingHomeImage}
                          style={{ marginRight: '10px' }}
                        >
                          {uploadingHomeImage ? '⏳ Uploading...' : '⬆️ Upload Home Image'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setHomeImageFile(null)}
                          className="btn btn-secondary"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Display All Images */}
                {formData.images.length > 0 && (
                  <div style={{ marginTop: '20px', padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                    <label style={{ display: 'block', marginBottom: '15px', fontWeight: '600', color: '#333', fontSize: '15px' }}>
                      📷 All Product Images ({formData.images.length}):
                    </label>
                    <div className="image-list">
                      {formData.images.map((img, index) => (
                        <div key={index} className="image-item">
                          <img 
                            src={img} 
                            alt={index === 0 ? 'Cover Image' : `Home Image ${index}`}
                            onError={(e) => {
                              console.error('Failed to load image:', img);
                              e.target.style.display = 'none';
                              const errorDiv = document.createElement('div');
                              errorDiv.style.cssText = 'padding: 20px; text-align: center; color: #999; font-size: 12px;';
                              errorDiv.textContent = 'Image failed to load';
                              e.target.parentElement.appendChild(errorDiv);
                            }}
                          />
                          <div style={{ padding: '8px', fontSize: '12px', color: '#666', textAlign: 'center', wordBreak: 'break-all', fontWeight: index === 0 ? '600' : 'normal' }}>
                            {index === 0 ? '🖼️ Cover Image' : `🏠 Home Image ${index}`}
                          </div>
                          <button
                            type="button"
                            onClick={() => index === 0 ? handleRemoveCoverImage() : handleRemoveHomeImage(index - 1)}
                            className="btn-remove-image"
                            title={index === 0 ? 'Remove cover image' : `Remove home image ${index}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                  </div>
                </div>
              )}

              {/* Additional Info Tab */}
              {activeTab === 'additional' && (
                <div className="product-tab-content">
                  <div className="form-group product-form-full-width">
                    <label>More Info</label>
                    <textarea
                      name="moreInfo"
                      value={formData.moreInfo || ''}
                      onChange={handleInputChange}
                      rows="6"
                      placeholder="Additional product information..."
                    />
                  </div>
                  
                  <div className="form-group product-form-full-width">
                    <label>Care Guide</label>
                    <textarea
                      name="careGuide"
                      value={formData.careGuide || ''}
                      onChange={handleInputChange}
                      rows="6"
                      placeholder="Care instructions, maintenance tips..."
                    />
                  </div>
                </div>
              )}

              {/* Variants Tab */}
              {activeTab === 'variants' && (
                <div className="product-tab-content">
                  <div className="form-group product-form-full-width">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={formData.hasVariants}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            hasVariants: e.target.checked,
                            variants: e.target.checked ? formData.variants : []
                          });
                        }}
                      />
                      <span>Enable Product Variants</span>
                    </label>
                    <p className="form-help-text">
                      Enable this to create variants with different attributes (Size, Color, etc.), prices, and stock levels.
                    </p>
                  </div>

                  {formData.hasVariants && (
                    <>
                      {availableAttributes.length === 0 && (
                        <div style={{ 
                          padding: '15px', 
                          backgroundColor: '#fff3cd', 
                          border: '1px solid #ffc107', 
                          borderRadius: '8px',
                          marginBottom: '20px'
                        }}>
                          <strong>⚠️ No Attributes Available</strong>
                          <p style={{ margin: '5px 0 0 0', fontSize: '14px' }}>
                            You need to create attributes first before adding variants. 
                            <a href="/attributes" target="_blank" style={{ marginLeft: '5px', color: '#007bff' }}>
                              Go to Attribute Manager →
                            </a>
                          </p>
                        </div>
                      )}
                      <div className="variants-section">
                        <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: 'bold' }}>
                          Product Variants ({formData.variants?.length || 0})
                        </h3>

                        {/* Variant Form */}
                        <div className="variant-form" style={{ 
                          border: '1px solid #ddd', 
                          padding: '20px', 
                          borderRadius: '8px', 
                          marginBottom: '20px',
                          backgroundColor: '#f9f9f9'
                        }}>
                          <h4 style={{ marginBottom: '15px' }}>
                            {editingVariantIndex !== null ? 'Edit Variant' : 'Add New Variant'}
                          </h4>

                          {/* Attribute Selection */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Attributes *</label>
                            <div className="attributes-input">
                              {availableAttributes.length > 0 ? (
                                availableAttributes.map((attr) => (
                                  <div key={attr._id} style={{ marginBottom: '10px' }}>
                                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                                      {attr.name}:
                                    </label>
                                    <select
                                      value={variantForm.attributes.find(a => a.attributeName === attr.name)?.value || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        const newAttributes = variantForm.attributes.filter(a => a.attributeName !== attr.name);
                                        if (value) {
                                          newAttributes.push({ attributeName: attr.name, value });
                                        }
                                        setVariantForm({ ...variantForm, attributes: newAttributes });
                                      }}
                                      style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                    >
                                      <option value="">Select {attr.name}</option>
                                      {attr.values?.map((val) => (
                                        <option key={val._id || val.value} value={val.value || val}>
                                          {val.value || val} 
                                          {val.priceModifier ? ` (${val.priceModifier > 0 ? '+' : ''}${val.priceModifier})` : ''}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                ))
                              ) : (
                                <p style={{ color: '#666', fontStyle: 'italic' }}>
                                  No attributes available. Create attributes first in Attribute Manager.
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Variant Price */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Variant Price (leave empty to use base price)</label>
                            <input
                              type="number"
                              step="0.01"
                              value={variantForm.price || ''}
                              onChange={(e) => setVariantForm({ ...variantForm, price: e.target.value })}
                              placeholder="Optional - uses base price if empty"
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                          </div>

                          {/* Variant Discount */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Variant Discount (%)</label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={variantForm.discount || 0}
                              onChange={(e) => {
                                const discountValue = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                                setVariantForm({ ...variantForm, discount: discountValue });
                              }}
                              placeholder="0"
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                            {variantForm.price && variantForm.discount > 0 && (
                              <p style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                                Discounted Price: ₹{((parseFloat(variantForm.price) || 0) * (1 - (variantForm.discount || 0) / 100)).toFixed(2)}
                              </p>
                            )}
                          </div>

                          {/* Variant Stock */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Variant Stock (leave empty to use base stock)</label>
                            <input
                              type="number"
                              value={variantForm.stock || ''}
                              onChange={(e) => setVariantForm({ ...variantForm, stock: e.target.value })}
                              placeholder="Optional - uses base stock if empty"
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                          </div>

                          {/* Variant SKU */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Variant SKU</label>
                            <input
                              type="text"
                              value={variantForm.sku || ''}
                              onChange={(e) => setVariantForm({ ...variantForm, sku: e.target.value })}
                              placeholder="Optional - unique SKU for this variant"
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                          </div>

                          {/* Variant Image */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label>Variant Image URL</label>
                            <input
                              type="text"
                              value={variantForm.image || ''}
                              onChange={(e) => setVariantForm({ ...variantForm, image: e.target.value })}
                              placeholder="Optional - image URL for this variant"
                              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                            />
                          </div>

                          {/* Variant Active Status */}
                          <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input
                                type="checkbox"
                                checked={variantForm.isActive}
                                onChange={(e) => setVariantForm({ ...variantForm, isActive: e.target.checked })}
                              />
                              <span>Variant is Active</span>
                            </label>
                          </div>

                          {/* Variant Actions */}
                          <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                              type="button"
                              onClick={() => {
                                if (variantForm.attributes.length === 0) {
                                  toast.error('Please select at least one attribute');
                                  return;
                                }

                                const variantPrice = variantForm.price ? parseFloat(variantForm.price) : undefined;
                                const variantDiscount = variantForm.discount ? parseFloat(variantForm.discount) : 0;
                                
                                // Calculate discounted price if discount is set
                                let discountedPrice = undefined;
                                if (variantPrice !== undefined && variantDiscount > 0) {
                                  discountedPrice = variantPrice * (1 - variantDiscount / 100);
                                }

                                const newVariant = {
                                  attributes: variantForm.attributes,
                                  price: variantPrice,
                                  discount: variantDiscount > 0 ? variantDiscount : undefined,
                                  discountedPrice: discountedPrice,
                                  stock: variantForm.stock ? parseInt(variantForm.stock) : undefined,
                                  sku: variantForm.sku || undefined,
                                  image: variantForm.image || undefined,
                                  isActive: variantForm.isActive
                                };

                                if (editingVariantIndex !== null) {
                                  // Update existing variant
                                  const updatedVariants = [...formData.variants];
                                  updatedVariants[editingVariantIndex] = newVariant;
                                  setFormData({ ...formData, variants: updatedVariants });
                                  setEditingVariantIndex(null);
                                } else {
                                  // Add new variant
                                  setFormData({
                                    ...formData,
                                    variants: [...(formData.variants || []), newVariant]
                                  });
                                }

                                // Reset form
                                setVariantForm({
                                  attributes: [],
                                  price: '',
                                  discount: 0,
                                  stock: '',
                                  sku: '',
                                  image: '',
                                  isActive: true
                                });
                                toast.success(editingVariantIndex !== null ? 'Variant updated' : 'Variant added');
                              }}
                              className="btn btn-primary"
                              disabled={variantForm.attributes.length === 0}
                            >
                              {editingVariantIndex !== null ? 'Update Variant' : 'Add Variant'}
                            </button>
                            {editingVariantIndex !== null && (
                              <button
                                type="button"
                                onClick={() => {
                                  setVariantForm({
                                    attributes: [],
                                    price: '',
                                    discount: 0,
                                    stock: '',
                                    sku: '',
                                    image: '',
                                    isActive: true
                                  });
                                  setEditingVariantIndex(null);
                                }}
                                className="btn btn-secondary"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Variants List */}
                        {formData.variants && formData.variants.length > 0 && (
                          <div className="variants-list">
                            <h4 style={{ marginBottom: '15px' }}>Existing Variants:</h4>
                            <div style={{ display: 'grid', gap: '10px' }}>
                              {formData.variants.map((variant, index) => (
                                <div
                                  key={index}
                                  style={{
                                    border: '1px solid #ddd',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    backgroundColor: 'white',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                  }}
                                >
                                  <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                                      {variant.attributes && variant.attributes.length > 0 ? (
                                        variant.attributes.map(a => a.value || a).join(' / ')
                                      ) : (
                                        'No attributes'
                                      )}
                                    </div>
                                    {variant.attributes && variant.attributes.length > 0 && (
                                      <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                                        ({variant.attributes.map(a => `${a.attributeName || 'Attribute'}: ${a.value || a}`).join(', ')})
                                      </div>
                                    )}
                                    <div style={{ fontSize: '14px', color: '#666' }}>
                                      {variant.price !== undefined && (
                                        <>
                                          {variant.discount > 0 && variant.discountedPrice !== undefined ? (
                                            <span>
                                              Price: <span style={{ textDecoration: 'line-through', color: '#999' }}>₹{variant.price.toLocaleString()}</span>{' '}
                                              <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>₹{variant.discountedPrice.toFixed(2)}</span>
                                              {' '}({variant.discount}% OFF) |{' '}
                                            </span>
                                          ) : (
                                            <span>Price: ₹{variant.price.toLocaleString()} | </span>
                                          )}
                                        </>
                                      )}
                                      {variant.stock !== undefined && (
                                        <span>Stock: {variant.stock} | </span>
                                      )}
                                      {variant.sku && <span>SKU: {variant.sku} | </span>}
                                      <span style={{ color: variant.isActive ? 'green' : 'red' }}>
                                        {variant.isActive ? 'Active' : 'Inactive'}
                                      </span>
                                    </div>
                                  </div>
                                  <div style={{ display: 'flex', gap: '5px' }}>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setVariantForm({
                                          attributes: variant.attributes,
                                          price: variant.price?.toString() || '',
                                          stock: variant.stock?.toString() || '',
                                          sku: variant.sku || '',
                                          image: variant.image || '',
                                          isActive: variant.isActive
                                        });
                                        setEditingVariantIndex(index);
                                      }}
                                      className="btn btn-sm btn-secondary"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updatedVariants = formData.variants.filter((_, i) => i !== index);
                                        setFormData({ ...formData, variants: updatedVariants });
                                        toast.success('Variant removed');
                                      }}
                                      className="btn btn-sm btn-danger"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="product-modal-footer product-form-full-width">
                <button type="button" onClick={closeModal} className="btn btn-secondary">
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                >
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />
    </div>
  );
};

export default ProductManager;

